import { produce, useTranslation } from "@opendash/core";
import { Icon } from "@opendash/icons";
import { Button, Flex, Transfer } from "antd";
import type { TransferDirection } from "antd/es/transfer";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useState } from "react";
import { Stop, Stop_Times, Trip } from "../js/parsegenerated";
import { AddTimesModal } from "./AddTimesModal";

interface StopDataType {
  key: string;
  title: string;
  description: string;
  arrival_time: Date;
  departure_time: Date;
  stop_sequence: number;
  chosen: boolean;
  disabled: boolean;
  stopTime: Stop_Times;
}

export const StopTransfer = observer((props: { id: string }) => {
  const t = useTranslation();
  const [stopsRecord, setStopsRecord] = useState<Array<StopDataType>>([]);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [lastMoveKey, setLastMoveKey] = useState<string>("");
  const [addTimesModal, setAddTimesModal] = useState<boolean>(false);
  const [currentStopTime, setCurrentStopTime] = useState<Stop_Times>();

  const onSelectChange = (
    sourceSelectedKeys: Array<string>,
    targetSelectedKeys: Array<string>
  ) => {
    // Disable all other entries because only one could be selected and transfered.

    //const stopsRecordCopy = stopsRecord.map();
    //console.log({ stopsRecordCopy });
    setStopsRecord(
      produce((draft) => {
        draft.forEach((rec) => {
          if (!sourceSelectedKeys.includes(rec.key)) {
            rec.disabled = sourceSelectedKeys.length > 0;
          }
          return rec;
        });
        return draft;
      })
    );
  };

  const onChange = async (
    targetkeys: string[],
    direction: TransferDirection,
    movekeys: string[]
  ) => {
    setTargetKeys(targetkeys);
    setLastMoveKey(movekeys[0]);
    if (direction === "right") {
      // Add stop to trip
      setAddTimesModal(true);
    } else if (direction === "left") {
      // Remove stop from trip
      for await (const movekey of movekeys) {
        const stoptimetodelete = await new Parse.Query(Stop_Times)
          .equalTo("trip_id", {
            __type: "Pointer",
            className: Trip.className,
            objectId: props.id,
          })
          .equalTo("stop_id", {
            __type: "Pointer",
            className: Stop.className,
            objectId: movekey,
          })
          .first();
        const removedStopTime = await stoptimetodelete.destroy();

        // Decrement all stop sequences of later stops
        const laterStops = await new Parse.Query(Stop_Times)
          .greaterThanOrEqualTo(
            "stop_sequence",
            removedStopTime.get("stop_sequence")
          )
          .equalTo("trip_id", removedStopTime.get("trip_id"))
          .find();

        const decrementedStopsPromise = laterStops.map((stop) => {
          stop.decrement("stop_sequence", 1);
          return stop.save();
        });
        await Promise.all(decrementedStopsPromise);
      }
    }

    getStopsAndStoptimes();
  };

  const onOkMove = () => {
    setAddTimesModal(false);
    getStopsAndStoptimes();
    setCurrentStopTime(undefined);
  };

  const onCancelMove = () => {
    // Deletes the key from the array of target keys if the modal is canceled.
    const newtargetkeys = targetKeys.slice();
    const x = newtargetkeys.findIndex((tk) => tk === lastMoveKey);
    if (x !== -1) {
      newtargetkeys.splice(x, 1);
    }
    setTargetKeys(newtargetkeys);
    setAddTimesModal(false);
    setCurrentStopTime(undefined);
  };

  const getStopsAndStoptimes = async () => {
    const stops = await new Parse.Query(Stop).find();
    const stoptimeobjects = await new Parse.Query(Stop_Times)
      .equalTo("trip_id", {
        __type: "Pointer",
        className: Trip.className,
        objectId: props.id,
      })
      .find();

    const records = stops.map((stop: Stop): StopDataType => {
      let arr_time = null;
      let dep_time = null;
      let stop_seq = 0;

      const matchingstoptimeobjects = stoptimeobjects.filter(
        (stoptimeobject) => stoptimeobject.get("stop_id").id === stop.id
      );

      if (matchingstoptimeobjects.length > 0) {
        arr_time = matchingstoptimeobjects[0].get("arrival_time");
        dep_time = matchingstoptimeobjects[0].get("departure_time");
        stop_seq = matchingstoptimeobjects[0].get("stop_sequence");
      }

      return {
        chosen: stoptimeIsExisting(stop),
        description: stop.get("stop_desc"),
        key: stop.id,
        title: stop.get("stop_name"),
        arrival_time: arr_time,
        departure_time: dep_time,
        stop_sequence: stop_seq,
        disabled: false,
        stopTime: matchingstoptimeobjects[0],
      };
    });

    setStopsRecord(records);
    setTargetKeys(
      records
        .filter((record) => record.chosen)
        .sort((a, b) => a.stop_sequence - b.stop_sequence)
        .map((record) => record.key)
    );

    function stoptimeIsExisting(stop: Stop) {
      return stoptimeobjects.find((stoptime) => stoptime.stop_id.id === stop.id)
        ? true
        : false;
    }
  };

  useEffect(() => {
    getStopsAndStoptimes();
  }, []);

  useEffect(() => {
    // Reorder all records based on its place in the route
    const newstate = []
      .concat(stopsRecord)
      .sort((a: StopDataType, b: StopDataType) => {
        if (a.stop_sequence < b.stop_sequence) {
          return -1;
        }
        if (a.stop_sequence > b.stop_sequence) {
          return 1;
        }
        return 0;
      });

    setStopsRecord(newstate);
  }, [targetKeys.length]);

  return (
    <>
      <Transfer
        showSearch
        showSelectAll={false}
        listStyle={{
          width: "50%",
          height: 300,
        }}
        titles={[
          t("gtfs:trip.stops.transfer.titles.left"),
          t("gtfs:trip.stops.transfer.titles.right"),
        ]}
        dataSource={stopsRecord}
        filterOption={(inputValue: string, option: StopDataType) =>
          option.title
            .toLocaleLowerCase()
            .indexOf(inputValue.toLocaleLowerCase()) > -1
        }
        targetKeys={targetKeys}
        onSelectChange={onSelectChange}
        onChange={onChange}
        render={(item) => {
          if (item.arrival_time !== null && item.departure_time !== null) {
            return (
              <Flex justify="space-between">
                {`${item.stop_sequence}: ${
                  item.title
                }: ${dayjs(item.arrival_time).format("HH:mm")} - ${dayjs(item.departure_time).format("HH:mm")}`}
                <Button
                  shape="circle"
                  onClick={() => {
                    setLastMoveKey(item.key);
                    setCurrentStopTime(item.stopTime);
                  }}
                >
                  <Icon icon="fa:edit" />
                </Button>
              </Flex>
            );
          }
          return item.title;
        }}
      />

      <AddTimesModal
        open={addTimesModal || currentStopTime !== undefined}
        onOk={onOkMove}
        onCancel={onCancelMove}
        stopid={lastMoveKey}
        tripid={props.id}
        stopTime={currentStopTime}
      />
    </>
  );
});
